/**
 * api.js — Akili Brain: Network Layer
 * All fetch / SSE calls. Reads token from AkiliPortal.getToken().
 * Never touches the DOM — callers handle rendering.
 *
 * ── Self-Evolve additions ────────────────────────────────────────────────────
 *   Workflows    : getWorkflows · createWorkflow · deleteWorkflow
 *   PersonalFacts: getPersonalFacts · deletePersonalFact · deleteAllPersonalFacts
 */

import { ENDPOINT, DRAFT_ENDPOINT, PREP_ENDPOINT, CONV_ENDPOINT } from './state';

function getToken(): string {
  return (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.getToken)
    ? (window as any).AkiliPortal.getToken()
    : '';
}

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  return { 'Authorization': 'Bearer ' + getToken(), ...extra };
}

interface FetchOptions extends RequestInit {
  headers?: Record<string, string>;
}

// ── Shared apiFetch wrapper ───────────────────────────────────────────────────

function apiFetch(url: string, opts: FetchOptions = {}): Promise<any> {
  if (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch) {
    return (window as any).AkiliPortal.apiFetch(url, opts).then((r: Response) => r.json());
  }
  // Fallback for standalone / test contexts
  return fetch(url, {
    ...opts,
    headers: authHeaders({ 'Content-Type': 'application/json', ...(opts.headers || {}) }),
  }).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  });
}

// ── Chat & Draft streaming ────────────────────────────────────────────────────

export async function chatStream(payload: any, onEvent: (evt: any) => void): Promise<void> {
  const res = await fetch(ENDPOINT, {
    method:  'POST',
    headers: authHeaders({
      'Content-Type': 'application/json',
      'Accept':       'text/event-stream',
    }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Server error ' + res.status);
  await _consumeSSE(res, onEvent);
}

export async function draftStream(payload: any, onEvent: (evt: any) => void): Promise<void> {
  const res = await fetch(DRAFT_ENDPOINT, {
    method:  'POST',
    headers: authHeaders({
      'Content-Type': 'application/json',
      'Accept':       'text/event-stream',
    }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Server error ' + res.status);
  await _consumeSSE(res, onEvent);
}

export async function prepStream(payload: any, onEvent: (evt: any) => void): Promise<void> {
  const res = await fetch(PREP_ENDPOINT, {
    method:  'POST',
    headers: authHeaders({
      'Content-Type': 'application/json',
      'Accept':       'text/event-stream',
    }),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Server error ' + res.status);
  await _consumeSSE(res, onEvent);
}

async function _consumeSSE(res: Response, onEvent: (evt: any) => void): Promise<void> {
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buf = '';

  while (true) {
    const chunk = await reader.read();
    if (chunk.done) break;
    buf += decoder.decode(chunk.value, { stream: true });

    const lines = buf.split('\n');
    buf = lines.pop() || '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const raw = line.slice(6).trim();
      try {
        onEvent(JSON.parse(raw));
      } catch (e) {
        console.warn('[AkiliBrain] SSE parse error:', line, e);
      }
    }
  }
}

// ── Conversations ────────────────────────────────────────────────────────────

export function getConversations(): Promise<any> {
  return (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch)
    ? (window as any).AkiliPortal.apiFetch(CONV_ENDPOINT).then((r: Response) => r.json())
    : Promise.reject(new Error('AkiliPortal unavailable'));
}

export function getConversation(id: string): Promise<any> {
  return (window as any).AkiliPortal.apiFetch(CONV_ENDPOINT + '/' + id).then((r: Response) => r.json());
}

export function deleteConversation(id: string): Promise<any> {
  return (window as any).AkiliPortal.apiFetch(CONV_ENDPOINT + '/' + id, { method: 'DELETE' });
}

// ── Feedback ──────────────────────────────────────────────────────────────────

export function sendFeedback(messageId: string, rating: number): Promise<any> {
  return (window as any).AkiliPortal.apiFetch('/api/ai/feedback', {
    method: 'POST',
    body:   JSON.stringify({ messageId: String(messageId), rating }),
  });
}

// ── Notifications / Insights ─────────────────────────────────────────────────

export function getNotifications(limit: number = 20): Promise<any> {
  return (window as any).AkiliPortal.apiFetch('/api/ai/notifications?limit=' + limit).then((r: Response) => r.json());
}

export function markNotificationRead(notifId: string): Promise<any> {
  return (window as any).AkiliPortal.apiFetch('/api/ai/notifications/' + notifId + '/read', { method: 'POST' });
}

export function markAllRead(): Promise<any> {
  return (window as any).AkiliPortal.apiFetch('/api/ai/notifications/mark-all-read', { method: 'POST' });
}

export function getUnreadCount(): Promise<any> {
  return (window as any).AkiliPortal.apiFetch('/api/ai/notifications/count').then((r: Response) => r.json());
}

// ── Transcription ─────────────────────────────────────────────────────────────

export async function transcribe(blob: Blob, mimeType: string): Promise<any> {
  const formData = new FormData();
  const ext = mimeType.includes('ogg') ? 'ogg' : mimeType.includes('mp4') ? 'mp4' : 'webm';
  formData.append('audio', blob, 'recording.' + ext);
  const res = await fetch('/api/ai/transcribe', {
    method:  'POST',
    headers: authHeaders(),
    body:    formData,
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.message || 'Transcription failed. Please try again.');
  }
  return res.json();
}

// ── Document embedding ────────────────────────────────────────────────────────

export function embedDocument(documentId: string, fileName: string): Promise<any> {
  return fetch('/api/ai/documents/embed', {
    method:  'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body:    JSON.stringify({ documentId, fileName }),
  });
}

// ── Proactive suggestions ─────────────────────────────────────────────────────

export function getProactiveSuggestions(): Promise<any> {
  return (window as any).AkiliPortal.apiFetch('/api/ai/proactive-suggestions').then((r: Response) => r.json());
}

export function dismissSuggestion(id: string): Promise<any> {
  return (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch)
    ? (window as any).AkiliPortal.apiFetch('/api/ai/dismiss-suggestion/' + encodeURIComponent(id), { method: 'POST' })
    : Promise.resolve();
}

// ── Workflows ─────────────────────────────────────────────────────────────────

export function getWorkflows(): Promise<any> {
  return apiFetch('/api/ai/workflows');
}

export function createWorkflow(payload: {
  name: string;
  workflow_type: string;
  cron_schedule: string;
  matter_id?: string;
  custom_prompt?: string;
}): Promise<any> {
  if (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch) {
    return (window as any).AkiliPortal.apiFetch('/api/ai/workflows', {
      method: 'POST',
      body:   JSON.stringify(payload),
    }).then((r: Response) => r.json());
  }
  return fetch('/api/ai/workflows', {
    method:  'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body:    JSON.stringify(payload),
  }).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  });
}

export function deleteWorkflow(id: string): Promise<any> {
  if (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch) {
    return (window as any).AkiliPortal.apiFetch('/api/ai/workflows/' + encodeURIComponent(id), { method: 'DELETE' });
  }
  return fetch('/api/ai/workflows/' + encodeURIComponent(id), {
    method:  'DELETE',
    headers: authHeaders(),
  }).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
  });
}

// ── Personal Facts ────────────────────────────────────────────────────────────

export function getPersonalFacts(): Promise<any> {
  return apiFetch('/api/ai/personal-facts');
}

export function deletePersonalFact(id: string): Promise<any> {
  if (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch) {
    return (window as any).AkiliPortal.apiFetch('/api/ai/personal-facts/' + encodeURIComponent(id), { method: 'DELETE' });
  }
  return fetch('/api/ai/personal-facts/' + encodeURIComponent(id), {
    method:  'DELETE',
    headers: authHeaders(),
  }).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
  });
}

export function deleteAllPersonalFacts(): Promise<any> {
  if (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch) {
    return (window as any).AkiliPortal.apiFetch('/api/ai/personal-facts', { method: 'DELETE' });
  }
  return fetch('/api/ai/personal-facts', {
    method:  'DELETE',
    headers: authHeaders(),
  }).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
  });
}

// ── Memory (Option B) — /api/user/memory ─────────────────────────────────────

export function getMemorySnapshot(): Promise<any> {
  return apiFetch('/api/user/memory');
}

export function deleteMemoryFact(id: string): Promise<any> {
  if (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch) {
    return (window as any).AkiliPortal.apiFetch('/api/user/memory/' + encodeURIComponent(id), { method: 'DELETE' });
  }
  return fetch('/api/user/memory/' + encodeURIComponent(id), {
    method:  'DELETE',
    headers: authHeaders(),
  }).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
  });
}

export function resetAllMemory(): Promise<any> {
  if (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch) {
    return (window as any).AkiliPortal.apiFetch('/api/user/memory/reset', { method: 'DELETE' });
  }
  return fetch('/api/user/memory/reset', {
    method:  'DELETE',
    headers: authHeaders(),
  }).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
  });
}

export function setMemoryEnabled(enabled: boolean): Promise<any> {
  const body = JSON.stringify({ paused: !enabled });
  if (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch) {
    return (window as any).AkiliPortal.apiFetch('/api/user/memory/pause', {
      method: 'POST',
      body,
    }).then((r: Response) => r.json());
  }
  return fetch('/api/user/memory/pause', {
    method:  'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body,
  }).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  });
}

export function toggleFactPin(factId: string, pinned: boolean): Promise<any> {
  const url = '/api/user/memory/' + encodeURIComponent(factId) + '/pin';
  if (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch) {
    return (window as any).AkiliPortal.apiFetch(url, {
      method: 'PATCH',
      body:   JSON.stringify({ pinned }),
    });
  }
  return fetch(url, {
    method:  'PATCH',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body:    JSON.stringify({ pinned }),
  }).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
  });
}

// ── [PHASE 4] Widget State ────────────────────────────────────────────────────

export function getWidgetState(): Promise<any> {
  if (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch) {
    return (window as any).AkiliPortal.apiFetch('/api/ai/widget-state').then((r: Response) => r.json());
  }
  return fetch('/api/ai/widget-state', {
    headers: authHeaders(),
  }).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  });
}

// ── [PHASE 2 & 5] Persona ─────────────────────────────────────────────────────

export function getPersona(): Promise<any> {
  if (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch) {
    return (window as any).AkiliPortal.apiFetch('/api/user/persona').then((r: Response) => r.json());
  }
  return fetch('/api/user/persona', {
    headers: authHeaders(),
  }).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  });
}

export function updatePersona(persona: string): Promise<any> {
  const body = JSON.stringify({ persona });
  if (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch) {
    return (window as any).AkiliPortal.apiFetch('/api/user/persona', {
      method: 'PUT',
      body,
    }).then((r: Response) => r.json());
  }
  return fetch('/api/user/persona', {
    method:  'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body,
  }).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  });
}

// ── [PHASE 3] Notification Settings ──────────────────────────────────────────

export function getNotificationSettings(): Promise<any> {
  if (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch) {
    return (window as any).AkiliPortal.apiFetch('/api/user/notification-settings').then((r: Response) => r.json());
  }
  return fetch('/api/user/notification-settings', {
    headers: authHeaders(),
  }).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  });
}

export function updateNotificationSettings(settings: object): Promise<any> {
  const body = JSON.stringify(settings);
  if (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch) {
    return (window as any).AkiliPortal.apiFetch('/api/user/notification-settings', {
      method: 'PUT',
      body,
    }).then((r: Response) => r.json());
  }
  return fetch('/api/user/notification-settings', {
    method:  'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body,
  }).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  });
}

// ── [PHASE 3] Daily Briefing ──────────────────────────────────────────────────

export function getTodayBriefing(): Promise<any> {
  if (typeof (window as any).AkiliPortal !== 'undefined' && (window as any).AkiliPortal.apiFetch) {
    return (window as any).AkiliPortal.apiFetch('/api/ai/briefing/today').then((r: Response) => r.json());
  }
  return fetch('/api/ai/briefing/today', {
    headers: authHeaders(),
  }).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  });
}